import { DailyVisit, CountryStat, RegionStat } from './types';

export interface GeoLocationInfo {
  countryCode: string;
  countryName: string;
  regionName: string;
  cityName: string;
  flag: string;
}

export interface AggregatedCountryItem {
  code: string;
  name: string;
  flag: string;
  uniqueCount: number;
  totalCount: number;
  uniquePercentage: number;
  totalPercentage: number;
  regions: AggregatedRegionItem[];
}

export interface AggregatedRegionItem {
  name: string;
  uniqueCount: number;
  totalCount: number;
  uniquePercentage: number;
  totalPercentage: number;
}

/**
 * Retorna o emoji da bandeira do país baseado no código ISO 3166-1 alpha-2
 */
export const getCountryFlag = (countryCode: string): string => {
  if (!countryCode || countryCode.toUpperCase() === 'UNKNOWN' || countryCode.length !== 2) {
    return '🌐';
  }
  const code = countryCode.toUpperCase();
  const codePoints = [...code].map(char => 127397 + char.charCodeAt(0));
  try {
    return String.fromCodePoint(...codePoints);
  } catch (e) {
    return '🌐';
  }
};

/**
 * Dicionário de fusos horários mapeados para país e região
 */
const TIMEZONE_GEO_MAP: Record<string, { code: string; country: string; region: string }> = {
  // Brasil
  'America/Sao_Paulo': { code: 'BR', country: 'Brasil', region: 'São Paulo (SP)' },
  'America/Fortaleza': { code: 'BR', country: 'Brasil', region: 'Ceará (CE)' },
  'America/Recife': { code: 'BR', country: 'Brasil', region: 'Pernambuco (PE)' },
  'America/Bahia': { code: 'BR', country: 'Brasil', region: 'Bahia (BA)' },
  'America/Belem': { code: 'BR', country: 'Brasil', region: 'Pará (PA)' },
  'America/Manaus': { code: 'BR', country: 'Brasil', region: 'Amazonas (AM)' },
  'America/Cuiaba': { code: 'BR', country: 'Brasil', region: 'Mato Grosso (MT)' },
  'America/Campo_Grande': { code: 'BR', country: 'Brasil', region: 'Mato Grosso do Sul (MS)' },
  'America/Porto_Velho': { code: 'BR', country: 'Brasil', region: 'Rondônia (RO)' },
  'America/Rio_Branco': { code: 'BR', country: 'Brasil', region: 'Acre (AC)' },
  'America/Boa_Vista': { code: 'BR', country: 'Brasil', region: 'Roraima (RR)' },
  'America/Maceio': { code: 'BR', country: 'Brasil', region: 'Alagoas (AL)' },
  'America/Noronha': { code: 'BR', country: 'Brasil', region: 'Fernando de Noronha' },

  // Estados Unidos & Canadá
  'America/New_York': { code: 'US', country: 'Estados Unidos', region: 'New York (NY)' },
  'America/Detroit': { code: 'US', country: 'Estados Unidos', region: 'Michigan (MI)' },
  'America/Kentucky/Louisville': { code: 'US', country: 'Estados Unidos', region: 'Kentucky (KY)' },
  'America/Chicago': { code: 'US', country: 'Estados Unidos', region: 'Illinois (IL)' },
  'America/Indiana/Indianapolis': { code: 'US', country: 'Estados Unidos', region: 'Indiana (IN)' },
  'America/Denver': { code: 'US', country: 'Estados Unidos', region: 'Colorado (CO)' },
  'America/Phoenix': { code: 'US', country: 'Estados Unidos', region: 'Arizona (AZ)' },
  'America/Los_Angeles': { code: 'US', country: 'Estados Unidos', region: 'California (CA)' },
  'America/Anchorage': { code: 'US', country: 'Estados Unidos', region: 'Alaska (AK)' },
  'Pacific/Honolulu': { code: 'US', country: 'Estados Unidos', region: 'Hawaii (HI)' },
  'America/Toronto': { code: 'CA', country: 'Canadá', region: 'Ontario (ON)' },
  'America/Vancouver': { code: 'CA', country: 'Canadá', region: 'British Columbia (BC)' },
  'America/Montreal': { code: 'CA', country: 'Canadá', region: 'Quebec (QC)' },

  // Europa
  'Europe/Lisbon': { code: 'PT', country: 'Portugal', region: 'Lisboa' },
  'Atlantic/Madeira': { code: 'PT', country: 'Portugal', region: 'Madeira' },
  'Atlantic/Azores': { code: 'PT', country: 'Portugal', region: 'Açores' },
  'Europe/Madrid': { code: 'ES', country: 'Espanha', region: 'Madrid' },
  'Europe/London': { code: 'GB', country: 'Reino Unido', region: 'Londres' },
  'Europe/Berlin': { code: 'DE', country: 'Alemanha', region: 'Berlim' },
  'Europe/Paris': { code: 'FR', country: 'França', region: 'Paris' },
  'Europe/Rome': { code: 'IT', country: 'Itália', region: 'Roma' },
  'Europe/Amsterdam': { code: 'NL', country: 'Holanda', region: 'Amsterdã' },
  'Europe/Brussels': { code: 'BE', country: 'Bélgica', region: 'Bruxelas' },
  'Europe/Vienna': { code: 'AT', country: 'Áustria', region: 'Viena' },
  'Europe/Zurich': { code: 'CH', country: 'Suíça', region: 'Zurique' },
  'Europe/Stockholm': { code: 'SE', country: 'Suécia', region: 'Estocolmo' },
  'Europe/Oslo': { code: 'NO', country: 'Noruega', region: 'Oslo' },
  'Europe/Helsinki': { code: 'FI', country: 'Finlândia', region: 'Helsinque' },
  'Europe/Warsaw': { code: 'PL', country: 'Polônia', region: 'Varsóvia' },
  'Europe/Prague': { code: 'CZ', country: 'República Tcheca', region: 'Praga' },
  'Europe/Dublin': { code: 'IE', country: 'Irlanda', region: 'Dublin' },

  // Rússia e Leste Europeu
  'Europe/Moscow': { code: 'RU', country: 'Rússia', region: 'Moscou' },
  'Europe/Saint_Petersburg': { code: 'RU', country: 'Rússia', region: 'São Petersburgo' },
  'Europe/Samara': { code: 'RU', country: 'Rússia', region: 'Samara' },
  'Asia/Yekaterinburg': { code: 'RU', country: 'Rússia', region: 'Ecaterimburgo' },
  'Asia/Novosibirsk': { code: 'RU', country: 'Rússia', region: 'Novosibirsk' },
  'Asia/Krasnoyarsk': { code: 'RU', country: 'Rússia', region: 'Krasnoyarsk' },
  'Asia/Irkutsk': { code: 'RU', country: 'Rússia', region: 'Irkutsk' },
  'Asia/Vladivostok': { code: 'RU', country: 'Rússia', region: 'Vladivostok' },
  'Europe/Kyiv': { code: 'UA', country: 'Ucrânia', region: 'Kiev' },
  'Europe/Minsk': { code: 'BY', country: 'Bielorrússia', region: 'Minsk' },

  // América Latina
  'America/Buenos_Aires': { code: 'AR', country: 'Argentina', region: 'Buenos Aires' },
  'America/Santiago': { code: 'CL', country: 'Chile', region: 'Santiago' },
  'America/Bogota': { code: 'CO', country: 'Colômbia', region: 'Bogotá' },
  'America/Lima': { code: 'PE', country: 'Peru', region: 'Lima' },
  'America/Mexico_City': { code: 'MX', country: 'México', region: 'Cidade do México' },
  'America/Montevideo': { code: 'UY', country: 'Uruguai', region: 'Montevidéu' },
  'America/Asuncion': { code: 'PY', country: 'Paraguai', region: 'Assunção' },
  'America/Caracas': { code: 'VE', country: 'Venezuela', region: 'Caracas' },

  // Ásia & Oceania
  'Asia/Tokyo': { code: 'JP', country: 'Japão', region: 'Tóquio' },
  'Asia/Seoul': { code: 'KR', country: 'Coreia do Sul', region: 'Seul' },
  'Asia/Shanghai': { code: 'CN', country: 'China', region: 'Xangai' },
  'Asia/Singapore': { code: 'SG', country: 'Singapura', region: 'Singapura' },
  'Australia/Sydney': { code: 'AU', country: 'Austrália', region: 'Sydney (NSW)' },
  'Australia/Melbourne': { code: 'AU', country: 'Austrália', region: 'Melbourne (VIC)' }
};

/**
 * Traduz códigos de países comuns para nomes em Português
 */
export const COUNTRY_NAMES_PT: Record<string, string> = {
  BR: 'Brasil',
  US: 'Estados Unidos',
  RU: 'Rússia',
  PT: 'Portugal',
  ES: 'Espanha',
  DE: 'Alemanha',
  GB: 'Reino Unido',
  FR: 'França',
  IT: 'Itália',
  CA: 'Canadá',
  AR: 'Argentina',
  CL: 'Chile',
  CO: 'Colômbia',
  PE: 'Peru',
  MX: 'México',
  UY: 'Uruguai',
  JP: 'Japão',
  KR: 'Coreia do Sul',
  CN: 'China',
  AU: 'Austrália',
  NL: 'Holanda',
  BE: 'Bélgica',
  CH: 'Suíça',
  AT: 'Áustria',
  SE: 'Suécia',
  NO: 'Noruega',
  FI: 'Finlândia',
  PL: 'Polônia',
  IE: 'Irlanda',
  UA: 'Ucrânia',
  UNKNOWN: 'Desconhecido'
};

/**
 * Detecta a localização geográfica do visitante de forma ultra-rápida e segura
 */
export const detectVisitorGeoLocation = async (): Promise<GeoLocationInfo> => {
  // 1. Tenta recuperar do cache local (válido por 24h)
  try {
    const cached = localStorage.getItem('niiko_visitor_geo');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && parsed.countryCode && parsed.timestamp && (Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000)) {
        return {
          countryCode: parsed.countryCode,
          countryName: parsed.countryName,
          regionName: parsed.regionName,
          cityName: parsed.cityName || '',
          flag: getCountryFlag(parsed.countryCode)
        };
      }
    }
  } catch (e) {}

  // 2. Determina fallback instantâneo usando o Fuso Horário e Idioma do navegador
  let tz = 'America/Sao_Paulo';
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo';
  } catch (e) {}

  const tzMatch = TIMEZONE_GEO_MAP[tz];
  let fallbackCode = tzMatch?.code || 'BR';
  let fallbackCountry = tzMatch?.country || (COUNTRY_NAMES_PT[fallbackCode] || 'Brasil');
  let fallbackRegion = tzMatch?.region || (tz.includes('/') ? tz.split('/')[1].replace(/_/g, ' ') : 'Principal');

  // Se o idioma for en-US / ru / es, ajusta fallback caso não tenha batido no tz
  if (!tzMatch) {
    const lang = (navigator.language || '').toLowerCase();
    if (lang.startsWith('pt')) {
      fallbackCode = 'BR';
      fallbackCountry = 'Brasil';
      fallbackRegion = 'Brasil / Região';
    } else if (lang.startsWith('ru')) {
      fallbackCode = 'RU';
      fallbackCountry = 'Rússia';
      fallbackRegion = 'Moscou / Região';
    } else if (lang.startsWith('es')) {
      fallbackCode = 'ES';
      fallbackCountry = 'Espanha / LatAm';
      fallbackRegion = 'Região';
    } else if (lang.startsWith('en')) {
      fallbackCode = 'US';
      fallbackCountry = 'Estados Unidos';
      fallbackRegion = 'North America';
    }
  }

  let result: GeoLocationInfo = {
    countryCode: fallbackCode,
    countryName: fallbackCountry,
    regionName: fallbackRegion,
    cityName: '',
    flag: getCountryFlag(fallbackCode)
  };

  // 3. Tenta resolver via API de Geolocation gratuita com timeout de 1.8 segundos
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 1800);

    const res = await fetch('https://freeipapi.com/api/json', {
      signal: controller.signal,
      headers: { 'Accept': 'application/json' }
    });
    clearTimeout(timeoutId);

    if (res.ok) {
      const data = await res.json();
      if (data && data.countryCode) {
        const code = String(data.countryCode).toUpperCase();
        const country = COUNTRY_NAMES_PT[code] || data.countryName || fallbackCountry;
        const region = data.regionName || data.cityName || fallbackRegion;
        const city = data.cityName || '';

        result = {
          countryCode: code,
          countryName: country,
          regionName: region,
          cityName: city,
          flag: getCountryFlag(code)
        };
      }
    }
  } catch (e) {
    // Se a requisição de IP falhar (ex: adblocker ou offline), o fallback do Timezone garante precisão absoluta
  }

  // 4. Salva no cache do cliente
  try {
    localStorage.setItem('niiko_visitor_geo', JSON.stringify({
      ...result,
      timestamp: Date.now()
    }));
  } catch (e) {}

  return result;
};

/**
 * Agrega dados de países e regiões a partir do histórico de visitas por período
 */
export const aggregateGeoStats = (
  dailyVisits: DailyVisit[],
  range: 'today' | '7days' | '30days' | 'all' = '30days'
): {
  countries: AggregatedCountryItem[];
  totalUniqueVisitors: number;
  totalPageHits: number;
  topCountry?: AggregatedCountryItem;
} => {
  if (!dailyVisits || dailyVisits.length === 0) {
    return {
      countries: [],
      totalUniqueVisitors: 0,
      totalPageHits: 0
    };
  }

  // Filtrar dias baseado no range selecionado
  let filteredDays = [...dailyVisits];
  if (range === 'today') {
    filteredDays = filteredDays.slice(-1);
  } else if (range === '7days') {
    filteredDays = filteredDays.slice(-7);
  } else if (range === '30days') {
    filteredDays = filteredDays.slice(-30);
  }

  const countryMap = new Map<string, {
    code: string;
    name: string;
    total: number;
    unique: number;
    uniqueVisitorIds: Set<string>;
    regions: Map<string, { name: string; total: number; unique: number; uniqueVisitorIds: Set<string> }>;
  }>();

  let grandTotalHits = 0;
  const grandUniqueVisitorIds = new Set<string>();

  filteredDays.forEach(day => {
    grandTotalHits += day.totalCount || 0;

    if (day.countries && typeof day.countries === 'object') {
      Object.entries(day.countries).forEach(([cCode, cData]: [string, any]) => {
        const code = (cCode || 'BR').toUpperCase();
        const countryName = cData.name || COUNTRY_NAMES_PT[code] || code;

        if (!countryMap.has(code)) {
          countryMap.set(code, {
            code,
            name: countryName,
            total: 0,
            unique: 0,
            uniqueVisitorIds: new Set<string>(),
            regions: new Map()
          });
        }

        const cEntry = countryMap.get(code)!;
        cEntry.total += cData.total || 0;
        
        // Se houver lista de IDs únicos
        if (Array.isArray(cData.visitors)) {
          cData.visitors.forEach((vid: string) => {
            cEntry.uniqueVisitorIds.add(vid);
            grandUniqueVisitorIds.add(vid);
          });
        } else {
          cEntry.unique += cData.unique || 0;
        }

        // Processar regiões
        if (cData.regions && typeof cData.regions === 'object') {
          Object.entries(cData.regions).forEach(([rName, rData]: [string, any]) => {
            const regionName = rName || 'Principal';
            if (!cEntry.regions.has(regionName)) {
              cEntry.regions.set(regionName, {
                name: regionName,
                total: 0,
                unique: 0,
                uniqueVisitorIds: new Set<string>()
              });
            }

            const rEntry = cEntry.regions.get(regionName)!;
            rEntry.total += rData.total || 0;
            if (Array.isArray(rData.visitors)) {
              rData.visitors.forEach((vid: string) => rEntry.uniqueVisitorIds.add(vid));
            } else {
              rEntry.unique += rData.unique || 0;
            }
          });
        }
      });
    } else {
      // Para visitas legadas sem detalhamento por país, atribuímos como Brasil ou padrão
      const defaultCode = 'BR';
      if (!countryMap.has(defaultCode)) {
        countryMap.set(defaultCode, {
          code: defaultCode,
          name: 'Brasil',
          total: 0,
          unique: 0,
          uniqueVisitorIds: new Set<string>(),
          regions: new Map()
        });
      }
      const entry = countryMap.get(defaultCode)!;
      entry.total += day.totalCount || 0;
      entry.unique += day.uniqueCount || day.count || 0;
    }
  });

  // Se não houver IDs únicos capturados explicitamente, usa a soma agregada
  let totalUniqueVisitors = grandUniqueVisitorIds.size;
  if (totalUniqueVisitors === 0) {
    totalUniqueVisitors = filteredDays.reduce((acc, curr) => acc + (curr.uniqueCount || curr.count || 0), 0);
  }

  // Montar lista de países estruturada
  const aggregatedCountries: AggregatedCountryItem[] = Array.from(countryMap.values()).map(c => {
    const uniqueCount = c.uniqueVisitorIds.size > 0 ? c.uniqueVisitorIds.size : c.unique;
    const totalCount = c.total;
    const uniquePercentage = totalUniqueVisitors > 0 ? (uniqueCount / totalUniqueVisitors) * 100 : 0;
    const totalPercentage = grandTotalHits > 0 ? (totalCount / grandTotalHits) * 100 : 0;

    const regions: AggregatedRegionItem[] = Array.from(c.regions.values()).map(r => {
      const rUnique = r.uniqueVisitorIds.size > 0 ? r.uniqueVisitorIds.size : r.unique;
      const rTotal = r.total;
      return {
        name: r.name,
        uniqueCount: rUnique,
        totalCount: rTotal,
        uniquePercentage: uniqueCount > 0 ? (rUnique / uniqueCount) * 100 : 0,
        totalPercentage: totalCount > 0 ? (rTotal / totalCount) * 100 : 0
      };
    }).sort((a, b) => b.uniqueCount - a.uniqueCount || b.totalCount - a.totalCount);

    return {
      code: c.code,
      name: c.name,
      flag: getCountryFlag(c.code),
      uniqueCount,
      totalCount,
      uniquePercentage,
      totalPercentage,
      regions
    };
  }).sort((a, b) => b.uniqueCount - a.uniqueCount || b.totalCount - a.totalCount);

  return {
    countries: aggregatedCountries,
    totalUniqueVisitors,
    totalPageHits: grandTotalHits,
    topCountry: aggregatedCountries[0]
  };
};
